import { useState, useEffect } from "react";
import { formatDate } from "@/lib/date";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api } from "@/api/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useApiKeysPaginated, type ApiKey } from "@/hooks/useApiKeysPaginated";
import { useMyApiKey, type IdentityResponse } from "@/hooks/useMyApiKey";
import { usePageState } from "@/hooks/usePageState";
import { useResetPageOnChange } from "@/hooks/useResetPageOnChange";
import { useSearchQueryState } from "@/hooks/useSearchQueryState";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Activity, CalendarDays, Clock, Hash, Key, KeyRound, Loader2, Plus, Copy, Check, Trash2, User, RefreshCw, Search, ShieldCheck, Terminal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";
import { TableSkeleton } from "@/components/ui/table-skeleton";
import { TablePagination } from "@/components/ui/table-pagination";

export function MemberApiKeysSection() {
  return <MemberApiKeysView />;
}

function MemberApiKeysView() {
  const { t } = useTranslation();
  const { myKey, loading, error, refetch } = useMyApiKey();
  const [createdKey, setCreatedKey] = useState<{ id: string; key: string; prefix: string } | null>(null);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [identity, setIdentity] = useState<IdentityResponse | null>(null);

  useEffect(() => {
    api<IdentityResponse>("/api/v2/identity/me")
      .then(id => setIdentity(id))
      .catch(() => {});
  }, []);

  const handleCreate = async () => {
    try {
      const result = await api<{ id: string; key: string; prefix: string }>("/api/v2/api-keys/self", {
        method: "POST",
        body: JSON.stringify({ account_id: identity?.account_id }),
      });
      setCreatedKey(result);
      await refetch();
      toast.success(t("apiKeys.member.toast.created"));
    } catch {
      toast.error(t("apiKeys.member.toast.createError"));
    }
  };

  const handleRegenerate = async () => {
    try {
      const result = await api<{ id: string; key: string; prefix: string }>("/api/v2/api-keys/me/regenerate", {
        method: "POST",
      });
      setCreatedKey(result);
      setRegenerateOpen(false);
      await refetch();
      toast.success(t("apiKeys.member.toast.regenerated"));
    } catch {
      toast.error(t("apiKeys.member.toast.regenerateError"));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={KeyRound}
        title={t("apiKeys.member.header.title")}
        subtitle={t("apiKeys.member.header.subtitle")}
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-8">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">{t("apiKeys.member.loading")}</p>
          </CardContent>
        </Card>
      ) : !myKey ? (
        /* Empty state — no key yet */
        <div className="space-y-4">
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted ring-1 ring-border">
                <Key className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="space-y-1">
                <p className="font-semibold">{t("apiKeys.member.empty.title")}</p>
                <p className="text-sm text-muted-foreground max-w-xs">
                  {t("apiKeys.member.empty.description")}
                </p>
              </div>
              <Button onClick={handleCreate} className="gap-2">
                <Plus className="h-4 w-4" />
                {t("apiKeys.member.createButton")}
              </Button>
            </CardContent>
          </Card>

          {/* How-to hint */}
          <Card className="bg-muted/40">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Terminal className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("apiKeys.member.hint.afterCreateLabel")}</p>
                  <p className="text-sm text-muted-foreground">{t("apiKeys.member.hint.afterCreatePrefix")}<code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs ring-1 ring-border">rai connect</code>{t("apiKeys.member.hint.afterCreateSuffix")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Key exists */
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-6">
              {/* Status badge + prefix */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/20">
                    <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <p className="font-mono text-sm font-medium">{myKey.prefix}</p>
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      {t("apiKeys.member.status.active")}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => setRegenerateOpen(true)}
                  variant="outline"
                  size="sm"
                  className="gap-1.5 shrink-0"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {t("apiKeys.member.regenerateButton")}
                </Button>
              </div>

              {/* Metadata grid */}
              <div className="grid grid-cols-2 gap-4 rounded-lg bg-muted/40 p-4">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("apiKeys.member.metadata.created")}</p>
                  <p className="mt-1 text-sm">{formatDate(myKey.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("apiKeys.member.metadata.lastUsed")}</p>
                  <p className="mt-1 text-sm">
                    {formatDate(myKey.last_used_at, t("apiKeys.member.metadata.never"))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CLI hint */}
          <Card className="bg-muted/40">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Terminal className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("apiKeys.member.hint.connectCliLabel")}</p>
                  <p className="text-sm text-muted-foreground">{t("apiKeys.member.hint.connectCliPrefix")}<code className="rounded bg-background px-1.5 py-0.5 font-mono text-xs ring-1 ring-border">rai connect</code>{t("apiKeys.member.hint.connectCliSuffix")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <AlertDialog open={regenerateOpen} onOpenChange={setRegenerateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("apiKeys.member.dialogs.regenerate.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("apiKeys.member.dialogs.regenerate.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("apiKeys.member.dialogs.regenerate.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerate}>{t("apiKeys.member.dialogs.regenerate.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ShowOnceDialog
        createdKey={createdKey}
        onClose={() => setCreatedKey(null)}
      />
    </div>
  );
}

export function ApiKeysPage() {
  const isAdmin = useIsAdmin();
  if (!isAdmin) return <MemberApiKeysView />;
  return <AdminApiKeysView />;
}

function AdminApiKeysView() {
  const { t } = useTranslation();
  const { page, pageSize, totalPages, setPage, handlePageSizeChange } = usePageState(10);
  const [search, setSearch] = useSearchQueryState();
  const debounced = useDebouncedValue(search, 300);
  useResetPageOnChange(debounced, setPage);
  const { keys: paginatedKeys, total, loading, error, refetch } = useApiKeysPaginated(page, pageSize, debounced);
  // list_api_keys_v2 filtra is_active==true incondicionalmente en el backend
  // (nunca devuelve keys revocadas), asi que total ya es el conteo de activas
  // (RAISE-14058) — no derivar de paginatedKeys, que solo cubre la pagina cargada.
  const activeCount = total;
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={KeyRound}
        title={t("apiKeys.admin.header.title")}
        subtitle={t("apiKeys.admin.header.subtitle")}
        stats={
          !loading && total > 0
            ? [
                { value: total, label: t("apiKeys.admin.stats.keys") },
                ...(activeCount > 0
                  ? [{ value: activeCount, label: t("apiKeys.admin.stats.active"), tone: "success" as const }]
                  : []),
              ]
            : undefined
        }
      />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8"
          placeholder={t("apiKeys.admin.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {!loading && total === 0 ? (
        <EmptyState
          icon={Key}
          title={t("apiKeys.admin.empty.title")}
          description={t("apiKeys.admin.empty.description")}
        />
      ) : (
        <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead><span className="flex items-center gap-1.5"><Hash className="h-3.5 w-3.5" />{t("apiKeys.admin.table.prefix")}</span></TableHead>
              <TableHead><span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{t("apiKeys.admin.table.member")}</span></TableHead>
              <TableHead><span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{t("apiKeys.admin.table.lastUsed")}</span></TableHead>
              <TableHead><span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{t("apiKeys.admin.table.created")}</span></TableHead>
              <TableHead><span className="flex items-center gap-1.5"><Activity className="h-3.5 w-3.5" />{t("apiKeys.admin.table.status")}</span></TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          {loading ? (
            <TableSkeleton columns={6} />
          ) : (
            <TableBody>
              {paginatedKeys.map((k) => (
                <TableRow key={k.id}>
                  <TableCell className="font-mono text-sm">{k.prefix}</TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{k.member_name}</p>
                    <p className="text-xs text-muted-foreground">{k.member_email}</p>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(k.last_used_at, t("apiKeys.member.metadata.never"))}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(k.created_at)}
                  </TableCell>
                  <TableCell>
                    {k.is_active ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        {t("apiKeys.admin.status.active")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                        <span className="h-2 w-2 rounded-full bg-red-400" />
                        {t("apiKeys.admin.status.revoked")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setRevokeTarget(k)}
                    >
                      <Trash2 className="h-4 w-4" />
                      {t("apiKeys.admin.revokeButton")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        <TablePagination
          currentPage={page}
          totalPages={totalPages(total)}
          onPrev={() => setPage((p) => p - 1)}
          onNext={() => setPage((p) => p + 1)}
          hasNext={page < totalPages(total)}
          hasPrev={page > 1}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
        </div>
      )}

      <RevokeDialog
        apiKey={revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={async () => {
          if (!revokeTarget) return;
          try {
            await api(`/api/v2/api-keys/${revokeTarget.id}`, { method: "DELETE" });
            toast.success(t("apiKeys.admin.toast.revokeSuccess"));
            setRevokeTarget(null);
            refetch();
          } catch {
            toast.error(t("apiKeys.admin.toast.revokeError"));
            setRevokeTarget(null);
          }
        }}
      />

    </div>
  );
}

function ShowOnceDialog({
  createdKey,
  onClose,
}: {
  createdKey: { id: string; key: string; prefix: string } | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Dialog open={!!createdKey} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("apiKeys.dialogs.showOnce.title")}</DialogTitle>
          <DialogDescription>
            {t("apiKeys.dialogs.showOnce.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-md bg-muted p-3">
          <code className="flex-1 break-all text-sm">{createdKey?.key}</code>
          <Button variant="ghost" size="sm" onClick={handleCopy}>
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>{t("apiKeys.dialogs.showOnce.doneButton")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeDialog({
  apiKey,
  onClose,
  onConfirm,
}: {
  apiKey: ApiKey | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  return (
    <AlertDialog open={!!apiKey} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("apiKeys.dialogs.revoke.title")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("apiKeys.dialogs.revoke.descriptionPrefix")}<strong>{apiKey?.prefix}</strong>{t("apiKeys.dialogs.revoke.descriptionSuffix")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("apiKeys.dialogs.revoke.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {t("apiKeys.dialogs.revoke.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
